import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { GuildProject, ProjectGiftResult, ProjectsResponse } from '../lib/types';
import { Bar, Button, EmptyState, Modal, Panel } from './ui';
import { GiveForm } from './GiveForm';
import { formatNumber, timeAgo } from '../lib/format';

/**
 * Guild Projects.
 *
 * A project is the guild-scale version of a Community Project, and it uses the
 * same giving path underneath — so the contribution rules a player already
 * learned there apply here unchanged.
 */
export function GuildProjects({ guildKey }: { guildKey: string }) {
  const { refresh } = useSession();
  const { push } = useToast();
  const state = useAsync<ProjectsResponse>(() => api.get(`/guilds/${guildKey}/projects`), [guildKey]);
  const [giving, setGiving] = useState<GuildProject | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function start(templateKey: string, name: string) {
    setBusy(templateKey);
    try {
      await api.post(`/guilds/${guildKey}/projects`, { templateKey });
      push('success', `${name} is under way.`, '⚒');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not start that.');
    } finally {
      setBusy(null);
    }
  }

  const data = state.data;

  return (
    <div className="stack">
      <Panel
        title="⚒ Under way"
        hint={data ? `${data.running.length} / ${data.limits.maxActive}` : ''}
      >
        {state.loading && !data ? (
          <div className="pxwell skeleton" style={{ height: 140 }} />
        ) : (data?.running.length ?? 0) === 0 ? (
          <EmptyState art="⚒" title="Nothing on the go">
            {data?.you.canStart
              ? 'Pick something from below and the guild can start chipping at it.'
              : 'An officer can start one from the list below.'}
          </EmptyState>
        ) : (
          <div className="stack">
            {data!.running.map((project) => (
              <div key={project.id} className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
                <div className="row row--between row--wrap">
                  <div>
                    <strong>{project.name}</strong>
                  </div>
                  {project.expiresAt && (
                    <span className="tiny muted">ends {timeAgo(project.expiresAt)}</span>
                  )}
                </div>
                <p className="tiny muted" style={{ margin: 0 }}>
                  {project.description}
                </p>
                <Bar ratio={project.ratio} variant="egg" />
                <div className="row row--between tiny mono">
                  <span>
                    {formatNumber(project.progress)} / {formatNumber(project.goal)}
                  </span>
                  <span className="muted">
                    {project.myPoints > 0 ? `you: ${formatNumber(project.myPoints)}` : 'not helped yet'}
                  </span>
                </div>
                <div className="row row--between row--wrap">
                  <span className="row row--wrap tiny muted" style={{ gap: 4 }}>
                    {project.rewards.coins ? (
                      <span className="chip">+{formatNumber(project.rewards.coins)} coins each</span>
                    ) : null}
                    {project.rewards.guildExp ? (
                      <span className="chip">+{formatNumber(project.rewards.guildExp)} guild EXP</span>
                    ) : null}
                    {project.rewards.projectPoints ? (
                      <span className="chip">
                        +{formatNumber(project.rewards.projectPoints)} to the Community Project
                      </span>
                    ) : null}
                    {project.rewards.title ? <span className="chip">title: {project.rewards.title}</span> : null}
                    {/* The capacity reward, and the item reward, were both
                        invisible here — which is how a project called "Expand
                        the Nursery" came to be read as giving nothing at all.
                        A reward the card does not name is a reward a player
                        has to take on trust. */}
                    {project.rewards.nurserySlots ? (
                      <span className="chip chip--gold">
                        +{project.rewards.nurserySlots} Nursery slot
                        {project.rewards.nurserySlots > 1 ? 's' : ''} each
                      </span>
                    ) : null}
                    {(project.rewards.itemKeys ?? []).length > 0 ? (
                      <span className="chip">
                        {project.rewards.itemKeys!.length} item
                        {project.rewards.itemKeys!.length > 1 ? 's' : ''} each
                      </span>
                    ) : null}
                  </span>
                  {data!.you.isMember && (
                    <Button size="sm" variant="go" onClick={() => setGiving(project)}>
                      Contribute
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {data && data.available.length > 0 && (
        <Panel title="◈ Could be started" hint={data.you.canStart ? 'officers choose' : ''}>
          <div className="stack-sm">
            {data.available.map((template) => (
              <div key={template.key} className="pxwell row row--between row--wrap" style={{ padding: 'var(--gap-sm)' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong className="tiny">{template.name}</strong>
                  <p className="tiny muted" style={{ margin: '2px 0 0' }}>
                    {template.description}
                  </p>
                  <div className="tiny muted mono" style={{ marginTop: 4 }}>
                    goal {formatNumber(template.goal)}
                    {template.durationDays ? ` · ${template.durationDays} days` : ' · no deadline'}
                    {template.locked ? ` · needs guild level ${template.minGuildLevel}` : ''}
                  </div>
                </div>
                {data.you.canStart && (
                  <Button
                    size="sm"
                    disabled={template.locked || busy === template.key}
                    loading={busy === template.key}
                    onClick={() => void start(template.key, template.name)}
                  >
                    {template.locked ? 'Locked' : 'Start'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {data && data.completed.length > 0 && (
        <Panel title="✓ Finished" raised={false}>
          <div className="stack-sm">
            {data.completed.map((project) => (
              <div key={project.id} className="row row--between tiny">
                <span>{project.name}</span>
                <span className="muted">{timeAgo(project.completedAt!)}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {giving && (
        <Modal onClose={() => setGiving(null)} labelledBy="give-project" wide>
          <div className="stack">
            <div>
              <h2 id="give-project">{giving.name}</h2>
              <p className="tiny muted" style={{ marginTop: 4 }}>
                {formatNumber(giving.goal - giving.progress)} points still needed. Anything you give
                is gone for good, and up to{' '}
                {formatNumber(data?.limits.dailyPointsPerProject ?? 500)} points count from you here
                each day.
              </p>
            </div>
            <GiveForm
              onGive={async (gift) => {
                const result = await api.post<ProjectGiftResult>(
                  `/guilds/${guildKey}/projects/${giving.id}/give`,
                  gift,
                );
                push('reward', `${giving.name} +${formatNumber(result.pointsAdded)}.`, '⚒');
                if (result.completed) {
                  push('success', `${giving.name} is finished! Everyone who helped got their share.`, '★');
                }
                setGiving(null);
                state.reload();
                void refresh();
              }}
              onCancel={() => setGiving(null)}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
